import { useSentryContext } from 'contexts/sentry';
import { createHash } from 'crypto';
import { useEffect } from 'react';

/**
 * Generate a consistent anonymous ID from an IP address
 */
const generateAnonymousId = (ipAddress: string): string => {
  return createHash('md5').update(ipAddress).digest('hex').slice(0, 8);
};

/**
 * Hook that connects user identification to Hotjar and Sentry
 * If a user is logged in, uses their customer ID
 * If not, generates an anonymous ID from their IP address
 */
export const useHotjarSentryConnection = (userId?: string | number, ipAddress?: string) => {
  const { mergeUserData } = useSentryContext();

  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 2;
    const retryInterval = 2000; // 2 seconds
    let retryTimeout: NodeJS.Timeout;

    const identifyUser = () => {
      const hotjar = window.hj;

      if (hotjar) {
        // Determine user ID to use
        const finalUserId = userId
          ? String(userId)
          : ipAddress
            ? generateAnonymousId(ipAddress)
            : 'anonymous';

        // Set up Hotjar identification
        hotjar('identify', finalUserId, {
          isAnonymous: !userId,
        });

        // Update Sentry with the same identification
        mergeUserData({
          hotjarId: finalUserId,
          isAnonymous: !userId,
        });
      } else if (retryCount < maxRetries) {
        retryCount++;
        retryTimeout = setTimeout(identifyUser, retryInterval);
      }
    };

    identifyUser();

    return () => {
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [userId, ipAddress, mergeUserData]);
};
